import React, {Fragment, useEffect, useRef, useState} from 'react';
import {Link} from "react-router-dom";
import Button from "../UI/Button";
import Modal from "../UI/Modal";
import ModalForm from "../UI/ModalForm";
import ModalFormInputRow from "../UI/ModalFormInputRow";
import Box from "../UI/Box";
import ListEdit from "../UI/ListEdit";
import Thumbnail from "../UI/Thumbnail";
import {toast} from "react-toastify";
import {useItem} from "../../hooks/useItem";
import {
    createItemVariant,
    deleteItemVariant,
    updateItemVariant
} from "../../api/itemApiClient";
import {ITEM_VARIANT_STOCK_CHANGED_ERROR_CODE} from "../../utility/utility";
import {compressImage} from "../../utility/compressImage";
import PropertyRowsEditor, {
    emptyPropertyRow,
    findDuplicatePropertyName,
    propertySaveErrorMessage,
    toPropertyList
} from "./PropertyRowsEditor";

const MAX_VARIANT_IMAGES = 5;

const VARIANT_LIST_HEADERS = ['Image', 'Properties', 'Stock', 'Actions'];

const VARIANT_LIST_COLUMNS = {
    functionParams : ['raw'],
    dataKeys       : ['imageThumb', 'properties', 'stock'],
};

// The API may leave out the images list of a variant that has none.
const imagesOf = (variant) => variant.images || [];

const formatProperties = (properties) => properties.map((property) => property.name + ': ' + property.value).join(', ');

const moveEntry = (list, fromIndex, toIndex) => {
    const next = [...list];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    return next;
};

// Native HTML5 drag-and-drop (no extra dependency).
const ImageStrip = (props) => {
    const [dragIndex, setDragIndex] = useState(null);

    const handleDrop = (index) => {
        if (dragIndex !== null && dragIndex !== index) {
            props.onMove(dragIndex, index);
        }
        setDragIndex(null);
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        event.target.value = '';

        if (file) {
            props.onAdd(file);
        }
    };

    return (
        <div>
            {props.images.length > 1 && (
                <p className="text-xs text-gray-400 mb-1">Drag to reorder</p>
            )}
            <div className="flex flex-wrap gap-2 mb-2">
                {props.images.map((image, index) => (
                    <div
                        key={image.url}
                        className="relative hover:cursor-move"
                        draggable={props.images.length > 1}
                        onDragStart={() => setDragIndex(index)}
                        onDragOver={(event) => event.preventDefault()}
                        onDrop={() => handleDrop(index)}
                    >
                        <img src={image.url} alt={'Variant image ' + (index + 1)} className="w-14 h-14 object-cover border rounded"/>
                        <div
                            className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:cursor-pointer"
                            title="Delete image"
                            onClick={() => props.onRemove(index)}
                        >
                            X
                        </div>
                    </div>
                ))}
            </div>
            {props.images.length >= MAX_VARIANT_IMAGES ? (
                <p className="text-sm text-gray-500">Max {MAX_VARIANT_IMAGES} reached</p>
            ) : (
                <input
                    type="file"
                    accept="image/jpeg,image/png,image/jpg,image/gif"
                    onChange={handleFileChange}
                    disabled={props.disabled}
                    className="text-sm"
                />
            )}
        </div>
    );
};

// One modal for both creating (variant is null) and editing a variant.
// Every change, images included, stays local until Save sends it in one request;
// Cancel just drops it.
const VariantFormModal = (props) => {
    const variant = props.variant;
    const [stockQuantity, setStockQuantity] = useState(variant ? variant.stockQuantity : 0);
    // The stock the form started from, so the API can tell whether a purchase
    // changed the stock the admin edited.
    const [loadedStockQuantity, setLoadedStockQuantity] = useState(variant ? variant.stockQuantity : 0);
    const [properties, setProperties] = useState(variant ? variant.properties : [emptyPropertyRow()]);
    // {url, itemVariantImageId} for a kept image, {url, file} for a new one.
    const [images, setImages] = useState(() => (variant ? imagesOf(variant) : []).map((image) => ({
        url                : image.url,
        itemVariantImageId : image.itemVariantImageId,
    })));
    const imagesRef = useRef(images);
    const isOpenRef = useRef(true);
    const [saving, setSaving] = useState(false);
    // Save waits for a compression, or the image would be left out.
    const [compressing, setCompressing] = useState(false);

    // Each preview URL of a new file is freed on remove or when the modal closes.
    imagesRef.current = images;
    useEffect(() => {
        isOpenRef.current = true;

        return () => {
            isOpenRef.current = false;
            imagesRef.current.forEach((image) => image.file && URL.revokeObjectURL(image.url));
        };
    }, []);

    // The preview is made from the compressed file, so it shows what gets saved.
    const handleAddImage = async (file) => {
        setCompressing(true);
        const compressed = await compressImage(file);

        // Closed meanwhile: a preview URL made now would never be freed.
        if (!isOpenRef.current) {
            return;
        }

        const url = URL.createObjectURL(compressed);
        setImages((previous) => [...previous, {url : url, file : compressed}]);
        setCompressing(false);
    };

    const handleRemoveImage = (index) => {
        if (images[index].file) {
            URL.revokeObjectURL(images[index].url);
        }
        setImages(images.filter((image, i) => i !== index));
    };

    const handleMoveImage = (fromIndex, toIndex) => {
        setImages((previous) => moveEntry(previous, fromIndex, toIndex));
    };

    const saveVariant = (quantity, propertyList) => variant
        ? updateItemVariant(variant.itemVariantId, {
            stockQuantity       : quantity,
            loadedStockQuantity : loadedStockQuantity,
            properties          : propertyList,
            images              : images,
        })
        : createItemVariant(props.itemId, {
            stockQuantity : quantity,
            properties    : propertyList,
            images        : images.map((image) => image.file),
        });

    // Starts the stock field from the current stock and keeps the other edits.
    // Save stays blocked until then, or it would send the stale stock again.
    const takeOverCurrentStock = () => props.onReload().then((freshItem) => {
        const freshVariant = freshItem && freshItem.variants.find((candidate) => candidate.itemVariantId === variant.itemVariantId);

        if (freshVariant) {
            setStockQuantity(freshVariant.stockQuantity);
            setLoadedStockQuantity(freshVariant.stockQuantity);
        }
        else {
            // Without the current stock every Save would hit the same conflict again.
            toast.error('The current stock could not be loaded. Close the variant and open it again.');
        }
        setSaving(false);
    });

    const handleSubmit = (event) => {
        event.preventDefault();

        // A second Save while one is running would create a new variant twice.
        if (saving) {
            return;
        }

        if (compressing) {
            toast.error('Wait until the image is processed, then save again.');
            return;
        }

        const duplicateName = findDuplicatePropertyName(properties);
        if (duplicateName !== null) {
            toast.error('Property "' + duplicateName + '" is used more than once.');
            return;
        }

        setSaving(true);

        // The API saves all or nothing, so a failure leaves nothing to reload.
        saveVariant(parseInt(stockQuantity, 10) || 0, toPropertyList(properties))
            .then(() => {
                toast.success('Success!');
                props.onSaved();
            })
            .catch((error) => {
                if (error.code === ITEM_VARIANT_STOCK_CHANGED_ERROR_CODE) {
                    toast.error('The stock changed since you opened this variant (someone bought it). Check it and save again.');
                    takeOverCurrentStock();
                    return;
                }

                setSaving(false);
                toast.error(propertySaveErrorMessage(error));
            });
    };

    return (
        <Modal>
            <form onSubmit={handleSubmit}>
                <ModalForm
                    title={variant ? 'Edit variant' : 'Add variant'}
                    saveButtonTitle="Save"
                    // Closing mid-save would let its later onSaved close another modal.
                    handleCancelButtonClick={saving ? undefined : props.onClose}
                >
                    <ModalFormInputRow title="Properties">
                        <PropertyRowsEditor properties={properties} onChange={setProperties}/>
                    </ModalFormInputRow>
                    <ModalFormInputRow title="Images">
                        <ImageStrip
                            images={images}
                            disabled={saving || compressing}
                            onAdd={handleAddImage}
                            onRemove={handleRemoveImage}
                            onMove={handleMoveImage}
                        />
                    </ModalFormInputRow>
                    <ModalFormInputRow title="Stock quantity">
                        <input
                            type="number"
                            value={stockQuantity}
                            onChange={(event) => setStockQuantity(event.target.value)}
                            className="border rounded-lg py-2 px-3 w-full text-gray-700 focus:outline-none focus:shadow-outline"
                        />
                    </ModalFormInputRow>
                    {variant && (
                        <p className="text-xs text-gray-500">Already bought items keep their original properties.</p>
                    )}
                </ModalForm>
            </form>
        </Modal>
    );
};

const ItemVariantList = (props) => {
    // undefined: modal closed, null: creating a variant, otherwise the edited variant's id.
    const [modalVariantId, setModalVariantId] = useState(undefined);
    const {item, reload} = useItem(props.itemId);
    const closeModal = () => setModalVariantId(undefined);

    // The modal copies the variant into its own state when it opens.
    const editedVariant = item && modalVariantId
        ? item.variants.find((variant) => variant.itemVariantId === modalVariantId)
        : null;

    const handleSaved = () => {
        closeModal();
        reload();
    };

    const handleDeleteIconClick = (variant) => {
        if (!window.confirm('Remove this variant? If it was already bought, it is hidden instead, and past orders keep it.')) {
            return;
        }

        deleteItemVariant(variant.itemVariantId)
            .then(() => {
                reload();
                toast.success('Success!');
            })
            .catch((error) => {
                toast.error(error.message);
            });
    };

    const variantList = (item ? item.variants : []).map((variant) => {
        const images = imagesOf(variant);

        return {
            raw        : variant,
            imageThumb : (
                <div className="flex items-center gap-2">
                    <Thumbnail src={images.length > 0 ? images[0].url : null} alt="Variant thumbnail"/>
                    {images.length > 1 && (
                        <span className="text-xs text-gray-500">{'+' + (images.length - 1)}</span>
                    )}
                </div>
            ),
            properties : formatProperties(variant.properties),
            stock      : variant.stockQuantity,
        };
    });

    return (
        <Fragment>
            <Link to={'/items'}>Back</Link>
            <Box title={item ? 'Variants for "' + item.name + '"' : 'Variants'}>
                <Button title="Add variant" handleClick={() => setModalVariantId(null)}/>
                <ListEdit
                    headers={VARIANT_LIST_HEADERS}
                    items={variantList}
                    columns={VARIANT_LIST_COLUMNS}
                    handleEditIconClick={(variant) => setModalVariantId(variant.itemVariantId)}
                    handleDeleteIconClick={handleDeleteIconClick}
                />
                {item && item.variants.length === 0 && (
                    <p className="text-gray-500">No variants.</p>
                )}
            </Box>
            {(modalVariantId === null || editedVariant) && (
                <VariantFormModal
                    key={modalVariantId ?? 'new'}
                    itemId={props.itemId}
                    variant={editedVariant}
                    onClose={closeModal}
                    onSaved={handleSaved}
                    onReload={reload}
                />
            )}
        </Fragment>
    );
};

export default ItemVariantList;
